import { N8nModule } from '../../types';

export const module5: N8nModule = {
  id: 'module5',
  title: 'Production Deployment',
  description: 'Deploy n8n to production using Docker, configure for scalability, implement security best practices, and set up monitoring.',
  duration: '1.5 hours',
  objectives: [
    'Deploy n8n to production with Docker',
    'Configure environment for scalability',
    'Implement security best practices',
    'Set up monitoring and backups',
    'Use queue mode for high-volume workflows',
    'Configure SSL/TLS for secure connections'
  ],
  content: `# Production Deployment

## Docker Deployment

Deploy n8n using Docker for consistent, scalable production environments.

### Production Docker Compose
- PostgreSQL database backend
- Volume persistence
- Environment variable management
- Network configuration

### Scalability
- Queue mode for distributed processing
- Worker nodes for parallel execution
- Load balancing strategies

## Security Best Practices

Protect your n8n instance and data.

### Authentication & Authorization
- Strong webhook authentication
- IP whitelisting
- Rate limiting
- HTTPS/SSL enforcement

### Credential Management
- Encrypted credential storage
- Environment variable secrets
- Vault integration for sensitive data

## Monitoring & Maintenance

Track performance and ensure reliability.

### Logging
- Centralized log aggregation
- Error tracking with Sentry
- Performance metrics

### Backups
- Database backup strategies
- Workflow export automation
- Disaster recovery planning
`,
  videoUrl: 'https://www.youtube.com/watch?v=example5',
  codeExamples: [
    {
      title: 'Production Docker Compose',
      explanation: 'Complete docker-compose.yml for production deployment',
      code: `version: '3.8'

services:
  postgres:
    image: postgres:14
    restart: unless-stopped
    environment:
      POSTGRES_USER: n8n
      POSTGRES_PASSWORD: n8npassword
      POSTGRES_DB: n8n
    volumes:
      - postgres-data:/var/lib/postgresql/data
    healthcheck:
      test: ['CMD-SHELL', 'pg_isready -U n8n']
      interval: 5s
      timeout: 5s
      retries: 10

  n8n:
    image: n8nio/n8n:latest
    restart: unless-stopped
    ports:
      - "5678:5678"
    environment:
      - DB_TYPE=postgresdb
      - DB_POSTGRESDB_HOST=postgres
      - DB_POSTGRESDB_PORT=5432
      - DB_POSTGRESDB_DATABASE=n8n
      - DB_POSTGRESDB_USER=n8n
      - DB_POSTGRESDB_PASSWORD=n8npassword
      - N8N_BASIC_AUTH_ACTIVE=true
      - N8N_BASIC_AUTH_USER=admin
      - N8N_BASIC_AUTH_PASSWORD=securepassword
      - N8N_HOST=n8n.yourdomain.com
      - N8N_PROTOCOL=https
      - WEBHOOK_URL=https://n8n.yourdomain.com/
      - EXECUTIONS_MODE=queue
      - QUEUE_BULL_REDIS_HOST=redis
    volumes:
      - n8n-data:/home/node/.n8n
    depends_on:
      postgres:
        condition: service_healthy
      redis:
        condition: service_started

  redis:
    image: redis:7
    restart: unless-stopped
    volumes:
      - redis-data:/data

volumes:
  postgres-data:
  n8n-data:
  redis-data:`,
      language: 'yaml'
    },
    {
      title: 'Nginx Reverse Proxy Configuration',
      explanation: 'SSL termination and reverse proxy setup',
      code: `server {
    listen 80;
    server_name n8n.yourdomain.com;
    return 301 https://n8n.yourdomain.com$request_uri;
}

server {
    listen 443 ssl http2;
    server_name n8n.yourdomain.com;

    ssl_certificate /etc/letsencrypt/live/n8n.yourdomain.com/fullchain.pem;
    ssl_certificate_key /etc/letsencrypt/live/n8n.yourdomain.com/privkey.pem;

    location / {
        proxy_pass http://localhost:5678;
        proxy_set_header Host $host;
        proxy_set_header X-Real-IP $remote_addr;
        proxy_set_header X-Forwarded-For $proxy_add_x_forwarded_for;
        proxy_set_header X-Forwarded-Proto $scheme;
        
        # WebSocket support
        proxy_http_version 1.1;
        proxy_set_header Upgrade $http_upgrade;
        proxy_set_header Connection "upgrade";
    }
}`,
      language: 'nginx'
    }
  ],
  practiceExercise: {
    title: 'Deploy n8n to Production',
    description: 'Set up a complete production deployment with Docker Compose, PostgreSQL, queue mode, SSL, and monitoring.',
    requirements: [
      'Docker Compose configuration',
      'PostgreSQL database backend',
      'Queue mode enabled',
      'SSL/TLS configuration',
      'Backup strategy'
    ],
    hints: [
      'Use environment variables for secrets',
      'Test locally before deploying to production',
      'Set up health checks for all services'
    ]
  },
  quiz: {
    questions: [
      {
        id: 'q1-production-database',
        question: 'Which database is recommended for production n8n deployments?',
      options: ['SQLite', 'MySQL', 'PostgreSQL', 'MongoDB'],
      correctAnswer: 2,
      explanation: 'PostgreSQL is recommended for production due to its reliability, performance, and JSON support.'
      },
      {
        id: 'q2-queue-mode',
        question: 'What is queue mode in n8n?',
      options: [
        'A way to organize workflows',
        'Distributed processing using Redis for high-volume workflows',
        'A debugging feature',
        'A database optimization technique'
      ],
      correctAnswer: 1,
      explanation: 'Queue mode enables distributed processing using Redis, allowing multiple worker nodes to process workflows in parallel for better scalability.'
      }
    ]
  }
};
